import { Link } from '@tanstack/react-router';
import { ArrowRight, X } from 'lucide-react';
import type { Offer } from '@siliconvault/shared';
import { useAuth } from '../../hooks/useAuth';
import { useDismissOffer } from '../../hooks/useCommunity';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../i18n';
import styles from './OfferCard.module.css';

interface Props {
  offer: Offer;
}

const BADGE_CLASS: Record<string, string> = {
  pending: styles.badgePending,
  accepted: styles.badgeAccepted,
  declined: styles.badgeDeclined,
  withdrawn: styles.badgeWithdrawn,
};

export function OfferCard({ offer }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const dismissMutation = useDismissOffer();
  const isSender = offer.fromUserId === user?.userId;
  const isPending = offer.status === 'pending';

  const offeredItems = offer.items.filter((i) => i.side === 'offered');
  const requestedItems = offer.items.filter((i) => i.side === 'requested');

  // Pick the primary image — first requested item (what they want from you), or first offered
  const primaryItem = requestedItems[0] ?? offeredItems[0] ?? null;
  const primaryImage = primaryItem?.itemImageUrl ?? null;

  // Unique vault names from items
  const vaultNames = [...new Set(offer.items.map((i) => i.vaultName).filter(Boolean))] as string[];

  const badgeVariant = BADGE_CLASS[offer.status] || '';

  return (
    <Link to="/offers/$offerId" params={{ offerId: offer.id }} className={styles.card}>
      {/* Hero image area */}
      {primaryImage ? (
        <img src={primaryImage} alt={primaryItem?.itemName ?? ''} className={styles.image} loading="lazy" />
      ) : (
        <div className={styles.imagePlaceholder}>
          {offer.moneyAmount ? `${offer.moneyCurrency} ${offer.moneyAmount}` : t('community.tradeOffer')}
        </div>
      )}

      <div className={styles.body}>
        {/* Top: status badge + dismiss */}
        <div className={styles.topRow}>
          <span className={`${styles.badge} ${badgeVariant}`}>
            <span className={styles.badgeDot} />
            {offer.status}
          </span>
          {!isPending && (
            <button
              className={styles.dismissBtn}
              title={t('community.dismissOffer')}
              disabled={dismissMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dismissMutation.mutateAsync(offer.id)
                  .then(() => toast(t('community.offerDismissed'), 'success'))
                  .catch((err: any) => toast(err?.message || t('community.offerDismissFailed'), 'error'));
              }}
            >
              <X size={13} strokeWidth={2} />
            </button>
          )}
        </div>

        {/* Parties */}
        <div className={styles.parties}>
          <span className={styles.partyName}>{isSender ? t('community.you') : offer.fromUserName}</span>
          <ArrowRight size={12} strokeWidth={1.75} className={styles.partyArrow} />
          <span className={styles.partyName}>{isSender ? offer.toUserName : t('community.you')}</span>
        </div>

        {/* Item summary */}
        {primaryItem && (
          <div className={styles.itemName}>{primaryItem.itemName}</div>
        )}

        {/* Tags row: vault names, item count, money */}
        <div className={styles.meta}>
          {vaultNames.map((name) => (
            <span key={name} className={styles.vaultTag}>{name}</span>
          ))}
          {offeredItems.length > 0 && (
            <span className={styles.tag}>{offeredItems.length} {t('community.offering').toLowerCase()}</span>
          )}
          {requestedItems.length > 0 && (
            <span className={styles.tag}>{requestedItems.length} {t('community.requesting').toLowerCase()}</span>
          )}
          {offer.moneyAmount && (
            <span className={styles.moneyTag}>{offer.moneyCurrency} {offer.moneyAmount}</span>
          )}
        </div>

        {/* Footer: date */}
        <div className={styles.date}>{new Date(offer.createdAt).toLocaleDateString()}</div>
      </div>
    </Link>
  );
}
